import { distinctUntilChanged, fromEvent, map, merge, NEVER, Observable } from 'rxjs'

/**
 * Determine current page state.
 * Ref: https://developer.chrome.com/docs/web-platform/page-lifecycle-api
 *
 * Returns one of:
 * - `active`: user can see and is interacting with the page
 * - `passive`: user is not interacting with the page, but they can still see it
 * - `hidden` user cannot see and is not interacting with the page
 */
export const getPageState = (): 'active' | 'passive' | 'hidden' => {
  if (document.visibilityState === 'hidden') {
    return 'hidden'
  }
  if (document.hasFocus()) {
    return 'active'
  }
  return 'passive'
}

/**
 * Listen for events that accompany changes in page state. This does not monitor for `frozen` or
 * `terminated` page state.
 * Ref: https://developer.chrome.com/docs/web-platform/page-lifecycle-api#how_to_observe_state_changes
 */
export const pageStateChanges$: Observable<'active' | 'passive' | 'hidden'> =
  typeof window === 'undefined'
    ? NEVER
    : merge(
        fromEvent(window, 'blur', { capture: true }),
        fromEvent(window, 'focus', { capture: true }),
        fromEvent(window, 'pageshow', { capture: true }),
        fromEvent(window, 'resume', { capture: true }),
        fromEvent(window, 'visibilitychange', { capture: true }),
      ).pipe(
        map(() => getPageState()),
        distinctUntilChanged(),
      )
